import Color from "color";

const COLOR_RE =
  /^(#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})|rgba?\(.+\)|hsla?\(.+\))$/i;

// Syntactic check only. Named colours ("red") are deliberately not matched
// here, because expression labels such as "sand" or "snow" would match too.
export function isColorString(value: unknown): value is string {
  return typeof value === "string" && COLOR_RE.test(value.trim());
}

export function parseColor(value: unknown): Color | undefined {
  if (typeof value !== "string" || value.trim() === "") return undefined;
  try {
    return Color(value.trim());
  } catch (err) {
    return undefined;
  }
}

// Opaque colours become lower-case hex, the rest "rgba(r, g, b, a)".
export function normalizeColor(value: string): string {
  const color = parseColor(value);
  if (!color) return value;
  if (color.alpha() === 1) return color.hex().toLowerCase();
  return color.rgb().string();
}

// The same colour without transparency, as lower-case hex.
export function solidColor(value: string): string {
  const color = parseColor(value);
  return color ? color.alpha(1).hex().toLowerCase() : value;
}

export type ColorOccurrence = {
  path: (string | number)[];
  color: string;
  // Best-effort hint for labels, e.g. "grassland" or "zoom 11".
  context?: string;
};

type Path = (string | number)[];

function caseLabel(condition: unknown): string | undefined {
  if (!Array.isArray(condition)) return undefined;
  const [op, , operand] = condition;
  if (op === "in" && Array.isArray(operand) && operand[0] === "literal") {
    const list = operand[1];
    if (Array.isArray(list) && typeof list[0] === "string") return list[0];
  }
  if (op === "==" && typeof operand === "string") return operand;
  return undefined;
}

function matchLabel(label: unknown): string | undefined {
  if (typeof label === "string") return label;
  if (Array.isArray(label) && typeof label[0] === "string") return label[0];
  return undefined;
}

// Operators whose arguments are names (of a property, a state, a variable,
// an image), never colours, however much a name may look like one.
const NAME_OPERATORS = ["get", "has", "!has", "feature-state", "var", "image"];

// "zoom 11" for a zoom stop, the category for a categorical stop.
function stopLabel(key: unknown): string | undefined {
  if (typeof key === "number") return `zoom ${key}`;
  if (typeof key === "string") return key;
  return undefined;
}

function walk(
  value: unknown,
  path: Path,
  context: string | undefined,
  out: ColorOccurrence[]
) {
  if (isColorString(value)) {
    out.push({ path, color: value, context });
    return;
  }
  if (Array.isArray(value)) {
    const op = value[0];
    if (op === "match") {
      const last = value.length - 1;
      for (let i = 3; i < last; i += 2) {
        walk(value[i], [...path, i], matchLabel(value[i - 1]), out);
      }
      walk(value[last], [...path, last], "other", out);
    } else if (typeof op === "string" && op.startsWith("interpolate")) {
      for (let i = 4; i < value.length; i += 2) {
        walk(value[i], [...path, i], `zoom ${value[i - 1]}`, out);
      }
    } else if (op === "step") {
      walk(value[2], [...path, 2], context, out);
      for (let i = 4; i < value.length; i += 2) {
        walk(value[i], [...path, i], `zoom ${value[i - 1]}`, out);
      }
    } else if (op === "case") {
      const last = value.length - 1;
      for (let i = 2; i < last; i += 2) {
        walk(value[i], [...path, i], caseLabel(value[i - 1]), out);
      }
      walk(value[last], [...path, last], "other", out);
    } else if (!NAME_OPERATORS.includes(op)) {
      for (let i = 1; i < value.length; i++) {
        walk(value[i], [...path, i], context, out);
      }
    }
    return;
  }
  if (value && typeof value === "object") {
    // Legacy function: { stops: [[zoom or category, colour], ...] }
    const stops = (value as { stops?: unknown }).stops;
    if (Array.isArray(stops)) {
      stops.forEach((stop, i) => {
        if (Array.isArray(stop)) {
          walk(stop[1], [...path, "stops", i, 1], stopLabel(stop[0]), out);
        }
      });
    }
  }
}

// Lists every colour inside one paint value, with the path to reach it.
export function findColors(value: unknown): ColorOccurrence[] {
  // A bare named colour ("white") is only trusted as the whole paint value.
  if (typeof value === "string" && !isColorString(value)) {
    return parseColor(value) ? [{ path: [], color: value }] : [];
  }
  const out: ColorOccurrence[] = [];
  walk(value, [], undefined, out);
  return out;
}
